// @ts-nocheck
"use client";

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	type ColumnFiltersState,
	type SortingState,
	type VisibilityState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";
import { IconArrowDown, IconArrowUp, IconArrowsUpDown, IconColumns3, IconSearch } from "@tabler/icons-react"

import type { ExtendedColumnDef } from "@/components/tables/columns";
import { Skeleton } from "@/components/ui/skeleton";

interface DataTableProps<TData, TValue> {
	columns: ExtendedColumnDef<TData, TValue>[];
	data: TData[];
	usePagination: boolean;
	pageSize: number;
	loading: boolean;
	filterName?: string;
	filterColumn?: string;
	hiddenColumns?: string[];
}

interface LoadingDataTableProps<TData, TValue> {
	columns: ExtendedColumnDef<TData, TValue>[];
	rowCount: number;
	pagination: boolean;
	hiddenColumns?: string[];
}

export function DataTable<TData, TValue>({
	columns,
	data,
	usePagination,
	pageSize,
	loading,
	filterName,
	filterColumn,
	hiddenColumns,
}: DataTableProps<TData, TValue>) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
		hiddenColumns?.reduce((acc, col) => ({ ...acc, [col]: false }), {}) ?? {},
	);
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: pageSize ?? 10,
	});

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onColumnFiltersChange: setColumnFilters,
		onPaginationChange: setPagination,
		getFilteredRowModel: getFilteredRowModel(),
		state: {
			sorting,
			columnVisibility,
			columnFilters,
			pagination,
		},
	});
	const hideableColumns = table
		.getAllColumns()
		.filter((column) => column.getCanHide());

	return (
		<div>
			<div className="flex items-center justify-between py-4">
				{filterColumn ? (
					<div className="relative flex-1 max-w-sm">
						<IconSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder={`Search ${filterName ? filterName : filterColumn}...`}
							value={(table.getColumn(filterColumn)?.getFilterValue() as string) ?? ""}
							onChange={(event) => table.getColumn(filterColumn)?.setFilterValue(event.target.value)}
							className="pl-9"
						/>
					</div>
				) : (
					<div />
				)}
				{hideableColumns.length > 0 ? (
					<DropdownMenu>
						<DropdownMenuTrigger className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-input bg-background text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 gap-2">
							<IconColumns3 className="h-4 w-4" />
							Columns
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{table
								.getAllColumns()
								.filter((column) => column.getCanHide())
								.map((column) => {
									return (
										<DropdownMenuCheckboxItem
											key={column.id}
											className="capitalize"
											checked={column.getIsVisible()}
											onCheckedChange={(value) =>
												column.toggleVisibility(!!value)
											}
										>
											{column.columnDef.label || column.columnDef.header}
										</DropdownMenuCheckboxItem>
									);
								})}
						</DropdownMenuContent>
					</DropdownMenu>
				) : null}
			</div>
			<div className="overflow-hidden rounded-lg border border-border bg-muted/30 dark:bg-muted/20">
				<Table className="">
					<TableHeader className="bg-muted/50 [&_tr]:border-border">
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow className="border-border hover:bg-transparent" key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const enableSort = header.column.columnDef.enableSorting;
									return (
										<TableHead
											key={header.id}
											className="text-sm font-semibold text-foreground"
										>
											{enableSort ? (
												<button
													type="button"
													onClick={() =>
														header.column.toggleSorting(
															header.column.getIsSorted() === "asc",
														)
													}
													className="inline-flex cursor-pointer items-center bg-transparent font-semibold text-foreground shadow-none hover:bg-transparent"
												>
													{header.isPlaceholder
														? null
														: flexRender(
															header.column.columnDef.header,
															header.getContext(),
														)}
													{header.column.getIsSorted() === "asc" ? (
														<IconArrowUp className="w-4 h-4 ml-2" />
													) : header.column.getIsSorted() === "desc" ? (
														<IconArrowDown className="w-4 h-4 ml-2" />
													) : (
														<IconArrowsUpDown className="w-4 h-4 ml-2" />
													)}
												</button>
											) : header.isPlaceholder ? null : (
												flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)
											)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{loading ? (
							Array.from({ length: 3 }).map((_, i) => (
								<TableRow key={i} className="border-border odd:bg-muted/20">
									{Array.from({ length: columns.length }).map((_, index) => (
										<TableCell key={index} className="">
											<Skeleton className="h-8 w-full" />
										</TableCell>
									))}
								</TableRow>
							))
						) : table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => {
								return (
									<TableRow
										key={row.id}
										data-state={row.getIsSelected() && "selected"}
										className="border-border odd:bg-muted/15"
									>
										{row.getVisibleCells().map((cell) => {
											return (
												<TableCell
													key={cell.id}
													className="py-2 text-left text-foreground"
												>
													{Array.isArray(cell.getValue())
														? // biome-ignore lint/suspicious/noExplicitAny: Generic DataTable
														(cell.getValue() as unknown[]).map((item) => (
															<pre key={item.id}>{item}</pre>
														))
														: flexRender(
															cell.column.columnDef.cell,
															cell.getContext(),
														)}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="h-24 text-center text-muted-foreground"
								>
									No results.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>

			{usePagination ? (
				<div className="flex flex-col gap-4 py-4">
					<div className="w-full">
						<p className="h-10 px-4 py-2 text-center text-sm text-muted-foreground sm:text-left">
							Total Results: {table.getFilteredRowModel().rows.length}
						</p>
					</div>
					<div className="w-full flex justify-end gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
							className="flex-1 sm:flex-initial"
						>
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
							className="flex-1 sm:flex-initial"
						>
							Next
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}

export function LoadingDataTable<TData, TValue>({
	columns,
	rowCount,
	usePagination,
	pageSize,
}: LoadingDataTableProps<TData, TValue>) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: pageSize ?? 10,
	});
	const table = useReactTable({
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onPaginationChange: setPagination,
		state: {
			sorting,
			columnVisibility,
			pagination,
		},
	});
	const hideableColumns = table
		.getAllColumns()
		.filter((column) => column.getCanHide());
	return (
		<div>
			<div className="flex items-center justify-end py-4">
				{hideableColumns.length > 0 ? (
					<DropdownMenu>
						<DropdownMenuTrigger className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-input bg-background text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2">
							Columns
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{table
								.getAllColumns()
								.filter((column) => column.getCanHide())
								.map((column) => {
									return (
										<DropdownMenuCheckboxItem
											key={column.id}
											className="capitalize"
											checked={column.getIsVisible()}
											onCheckedChange={(value) =>
												column.toggleVisibility(!!value)
											}
										>
											{column.columnDef.label || column.columnDef.header}
										</DropdownMenuCheckboxItem>
									);
								})}
						</DropdownMenuContent>
					</DropdownMenu>
				) : null}
			</div>
			<div className="overflow-hidden rounded-lg border border-border bg-muted/30 dark:bg-muted/20">
				<Table className="">
					<TableHeader className="bg-muted/50 [&_tr]:border-border">
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow className="border-border hover:bg-transparent" key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead
											key={header.id}
											className="text-sm font-semibold text-foreground"
										>
											{header.isPlaceholder
												? null
												: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{Array.from(Array(rowCount).keys()).map((index) => (
							<TableRow key={index}>
								{Array.from(Array(columns.length).keys()).map((index) => (
									<TableCell key={index} className="">
										<Skeleton className="w-1/2 h-8 dark:bg-muted" />
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{usePagination ? (
				<div className="flex items-center py-4">
					<div className="mr-auto space-x-1 ">
						<p className="h-10 px-4 py-2 text-sm text-muted-foreground">
							Total Results: {table.getFilteredRowModel().rows.length}
						</p>
					</div>
					<div className="ml-auto space-x-1">
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
						>
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
						>
							Next
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}